/**
 * Policies list data table (OR-1664, design OR-1537) — column-driven.
 *
 * The set of columns comes from `policy-columns.ts`; the caller passes
 * which ids are visible and in what order (Customize). This component
 * lays them out — sortable headers and per-id cell rendering — with no
 * group band and no pinned column, matching the approved MVP table.
 *
 * Rows open the policy record. The Priority dropdown is the one write on
 * this screen:
 * optimistic, straight to `PATCH /v2/policies/{id}`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Loading02 } from "@untitledui/icons";
import { Select, Table, cx } from "@tricura/atlas-ui";
import { updatePolicyPriority } from "../../api";
import type { PolicyListItemWire, PolicyPriority } from "../../api";
import { Skeleton } from "../../components/skeleton";
import { PlainHeader, SortHeader } from "../../components/table-sort-header";
import { PRIORITY_LABELS } from "./filters";
import { PolicyStatusBadge as StatusBadge } from "./status-badge";
import { POLICY_COLUMNS, type PolicyColumnDef, allColumnIds, defaultVisibleIds } from "./policy-columns";

// ───────────────────────────── Priority cell ─────────────────────────────

/** The dropdown's "Select" entry: the unset state, which the design lists
 *  as a pickable option so a priority can be cleared again. */
const NONE = "none";

const PRIORITY_ITEMS: { id: PolicyPriority | typeof NONE; label: string }[] = [
    { id: "high", label: PRIORITY_LABELS.high },
    { id: "medium", label: PRIORITY_LABELS.medium },
    { id: "low", label: PRIORITY_LABELS.low },
    { id: NONE, label: "Select" },
];

function PriorityCell({ id, priority }: { id: string; priority: PolicyPriority | null }) {
    const [optimistic, setOptimistic] = useState<PolicyPriority | null | undefined>(undefined);
    const [pending, setPending] = useState(false);
    // The prop is the server's value at fetch time; the optimistic value
    // wins once the user has touched the row (no refetch after a save).
    const value = optimistic === undefined ? priority : optimistic;

    const apply = async (next: PolicyPriority | null) => {
        if (next === value) return;
        setOptimistic(next);
        setPending(true);
        try {
            await updatePolicyPriority(id, next);
        } catch (err) {
            console.error("Failed to update priority", err);
            setOptimistic(undefined);
        } finally {
            setPending(false);
        }
    };

    return (
        <div onClick={(e) => e.stopPropagation()} className="flex w-full min-w-0 items-center gap-2">
            {/* No colour on the priority column — Andressa kept it plain so it
              * doesn't compete with the Status badges. */}
            <Select
                size="sm"
                aria-label="Priority"
                placeholder="Select"
                selectedKey={value ?? null}
                items={PRIORITY_ITEMS}
                isDisabled={pending}
                className="min-w-0 flex-1"
                popoverClassName="min-w-max"
                data-autotest="policies-table-select-priority"
                onSelectionChange={(key) => {
                    if (key === null) return;
                    void apply(key === NONE ? null : (key as PolicyPriority));
                }}
            >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
            </Select>
            {pending && <Loading02 aria-label="Saving" className="size-4 text-fg-quaternary shrink-0 animate-spin" />}
        </div>
    );
}

// ───────────────────────────── Formatters ─────────────────────────────

/** ISO "2018-01-01" → "01/01/2018" (the design's date format). */
function formatDate(iso: string | null): string {
    if (!iso) return "—";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return iso;
    return `${m[2]}/${m[3]}/${m[1]}`;
}

const COMPACT_USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", minimumFractionDigits: 0, maximumFractionDigits: 1 });
const FULL_USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "$310K", "$1.2M" — the exact amount rides on the title for hover. */
function MoneyCell({ value }: { value: string }) {
    const n = Number(value);
    if (!Number.isFinite(n)) return <Placeholder />;
    return (
        <span className="text-secondary text-sm tabular-nums" title={FULL_USD.format(n)}>
            {COMPACT_USD.format(n)}
        </span>
    );
}

function Placeholder() {
    return <span className="text-tertiary text-sm">—</span>;
}

function TextCell({ value, className }: { value: string | null; className?: string }) {
    if (!value) return <Placeholder />;
    return (
        <span className={cx("text-secondary block truncate text-sm", className)} title={value}>
            {value}
        </span>
    );
}

/** Per-column-id cell renderer. */
function renderCell(columnId: string, row: PolicyListItemWire) {
    switch (columnId) {
        case "priority":
            return <PriorityCell id={row.id} priority={row.priority} />;
        case "policy_number":
            return <TextCell value={row.policy_number} className="tabular-nums" />;
        case "named_insured":
            return <TextCell value={row.named_insured} />;
        case "status":
            return <StatusBadge status={row.status} />;
        case "total_premium":
            return <MoneyCell value={row.total_premium} />;
        case "effective_date":
            return <span className="text-secondary text-sm tabular-nums">{formatDate(row.effective_date)}</span>;
        case "expiration_date":
            return <span className="text-secondary text-sm tabular-nums">{formatDate(row.expiration_date)}</span>;
        case "facility_count":
            return <span className="text-secondary text-sm tabular-nums">{row.facility_count}</span>;
        case "brokerage_name":
            return <TextCell value={row.brokerage_name} />;
        default:
            return <Placeholder />;
    }
}

// ───────────────────────────── Main table ─────────────────────────────

interface PoliciesTableProps {
    rows: PolicyListItemWire[];
    loading?: boolean;
    skeletonRows?: number;
    sort?: string;
    onSortChange?: (next: string | undefined) => void;
    /** Visible column ids (Customize). Defaults to the design's set. */
    visibleColumnIds?: string[];
    /** Column order ids (Customize drag-to-reorder). Defaults to natural order. */
    columnOrder?: string[];
}

export function PoliciesTable({ rows, loading = false, skeletonRows = 6, sort, onSortChange, visibleColumnIds, columnOrder }: PoliciesTableProps) {
    const handleSort = onSortChange ?? (() => undefined);
    const navigate = useNavigate();
    const termByPolicy = useMemo(() => new Map(rows.map((r) => [r.id, r.policy_term_id])), [rows]);

    // Visible columns in the user's order. No group ranking here: the MVP
    // table has no bands, so the order is exactly what Customize says.
    const columns = useMemo<PolicyColumnDef[]>(() => {
        const visible = new Set(visibleColumnIds ?? defaultVisibleIds());
        const order = columnOrder ?? allColumnIds();
        const rank = new Map(order.map((id, i) => [id, i]));
        return POLICY_COLUMNS.filter((c) => visible.has(c.id)).sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
    }, [visibleColumnIds, columnOrder]);

    const totalWidth = useMemo(() => columns.reduce((sum, c) => sum + c.width, 0), [columns]);

    // Stretch the columns to fill the wrapper when it is wider than their
    // declared total, so wide monitors don't leave dead space on the right.
    // Below that width the table scrolls horizontally.
    const tableRef = useRef<HTMLTableElement>(null);
    const [availWidth, setAvailWidth] = useState(0);
    useEffect(() => {
        const wrapper = tableRef.current?.parentElement;
        if (!wrapper) return;
        const ro = new ResizeObserver(([entry]) => {
            if (entry) setAvailWidth(entry.contentRect.width);
        });
        ro.observe(wrapper);
        setAvailWidth(wrapper.clientWidth);
        return () => ro.disconnect();
    }, []);

    const widthOf = useMemo(() => {
        const slack = Math.max(0, availWidth - totalWidth);
        return (c: PolicyColumnDef): number => (totalWidth === 0 || slack === 0 ? c.width : c.width + (slack * c.width) / totalWidth);
    }, [availWidth, totalWidth]);

    const cellStyle = (c: PolicyColumnDef): React.CSSProperties => {
        const w = widthOf(c);
        return { width: w, minWidth: w };
    };

    const skeletonItems = Array.from({ length: skeletonRows }, (_, idx) => ({ id: `skeleton-${idx}` }));

    return (
        <Table
            ref={tableRef}
            aria-label="Policies"
            data-autotest="policies-table"
            aria-busy={loading || undefined}
            // `table-fixed` makes columns honor their declared widths so long
            // insured names truncate instead of pushing the row wider.
            className="table-fixed"
            wrapperClassName="atlas-scrollbar flex-1 min-h-0 overflow-auto"
            style={{ minWidth: totalWidth }}
            onRowAction={(key) => {
                if (typeof key !== "string" || key.startsWith("skeleton-")) return;
                const term = termByPolicy.get(key);
                navigate(`/policies/${key}${term ? `?term=${term}` : ""}`);
            }}
        >
            <Table.Header className="bg-secondary sticky top-0 z-20">
                {columns.map((c, i) => (
                    <Table.Head key={c.id} id={c.id} isRowHeader={i === 0} style={cellStyle(c)} data-autotest={`policies-table-header-${c.id}`}>
                        {c.sortable ? (
                            <SortHeader field={c.id} sort={sort} onSortChange={handleSort}>
                                {c.label}
                            </SortHeader>
                        ) : (
                            <PlainHeader>{c.label}</PlainHeader>
                        )}
                    </Table.Head>
                ))}
            </Table.Header>

            {/* `dependencies`: react-aria caches rows by item, so the cells
              * must be rebuilt when the visible column set changes or the
              * collection throws "Cell count must match column count". */}
            {loading ? (
                <Table.Body items={skeletonItems} dependencies={[columns]}>
                    {(item) => (
                        <Table.Row id={item.id}>
                            {columns.map((c) => (
                                <Table.Cell key={c.id} style={cellStyle(c)}>
                                    <Skeleton className="h-4 w-20" />
                                </Table.Cell>
                            ))}
                        </Table.Row>
                    )}
                </Table.Body>
            ) : (
                <Table.Body items={rows} dependencies={[columns]}>
                    {(row) => (
                        <Table.Row id={row.id} data-autotest="policies-table-row" className="cursor-pointer">
                            {columns.map((c) => (
                                <Table.Cell key={c.id} style={cellStyle(c)} data-autotest={`policies-table-cell-${c.id}`}>
                                    {renderCell(c.id, row)}
                                </Table.Cell>
                            ))}
                        </Table.Row>
                    )}
                </Table.Body>
            )}
        </Table>
    );
}
